import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { BASE_PATH, getAccessToken } from "../api/api";
import { REACT_APP_WS_SOCKJS_URL } from "../config/runtimeConfig";
import { embedHostContext, embedRequestHeaders, embedRuntimeKey, isEmbedPageContentEnabled } from "./embedRuntime";
import { embedPageSummaryForSend, type PageSummary } from "./embedPageContent";

const API_BASE = BASE_PATH;
const baseUrl = API_BASE.replace(/\/$/, "");
const WS_SOCKJS =
  REACT_APP_WS_SOCKJS_URL || `${baseUrl}/ws-sockjs`;

export type ChatResponseContentType =
  | "table"
  | "chart"
  | "text"
  | "json"
  | "html"
  | "error"
  | "widget-draft"
  | "mutation-draft"
  | "forecast-plan"
  | "forecast-result"
  | "clarification";

export interface ClarificationQuestion {
  id: string;
  text: string;
  options?: string[];
}

export interface ChatOutputIntentPrediction {
  outputType?: string;
  chartType?: string;
  confidence?: number;
  scores?: Record<string, number>;
  source?: "user" | "predicted" | string;
}

export interface ChatResourceCandidate {
  kind?: "index" | "openapi" | "adaptor" | string;
  id?: string;
  name?: string;
  score?: number;
  /** 0–1 share among ranked candidates */
  probability?: number;
}

export interface ChatAutoSelection {
  source?: string;
  mode?: string;
  indices?: string[];
  indexDetails?: Array<{
    id?: string;
    name?: string;
    score?: number;
    probability?: number;
  }>;
  rankedIndices?: string[];
  openApiCatalogs?: Array<{ catalogId?: string; name?: string; tag?: string }>;
  adaptors?: Array<{
    adaptorId?: string;
    name?: string;
    type?: string;
    score?: number;
    probability?: number;
  }>;
  candidates?: ChatResourceCandidate[];
  outputIntent?: ChatOutputIntentPrediction;
  rationale?: string;
}

export type ChatQueryKind = "opensearch" | "sql" | "mongo";

/** A query that produced the answer: OpenSearch DSL or a read-only Data Adaptor query. */
export interface ChatExecutedQuery {
  kind: ChatQueryKind;
  tool?: string;
  /** OpenSearch only */
  indexName?: string;
  searchType?: string;
  /** Adaptor only */
  adaptorId?: string;
  adaptorName?: string;
  adaptorType?: string;
  /** DSL object (opensearch), SQL text, or Mongo command object */
  query: unknown;
  /** Warehouse query still running as a background job (see `WarehouseJobCard`); not a saved-example candidate. */
  jobId?: string;
  async?: boolean;
}

export interface ChatOrchestrationWebSocketPayload {
  username?: string;
  correlationId: string;
  type?: ChatResponseContentType | "auto-selection" | "progress" | "warehouse-job" | "chat-run";
  /** Declared widget/chat output format (table, echarts, …) — used when type and payload shape disagree. */
  outputFormat?: string;
  message?: string;
  result?: unknown;
  widgetDraft?: unknown;
  mutationDraft?: unknown;
  forecastPlan?: unknown;
  /** Pre-turn router picks — UI applies to index / OpenAPI / adaptor pickers for this turn. */
  autoSelection?: ChatAutoSelection;
  /** Successful OpenSearch / adaptor queries from this turn (for "save as example"). */
  executedQueries?: ChatExecutedQuery[];
  /** A tool needed a VPN one-time password this turn; the UI asks for it and resends the message. */
  vpnOtpRequired?: { vpnProfileId: string; vpnProfileName?: string };
  error?: string;
  /** Chat history message id for this assistant turn */
  messageId?: string;
  quotaExceeded?: boolean;
  period?: "daily" | "weekly" | "monthly" | "no_subscription";
  resetDate?: string;
  timestamp: string;
}

/**
 * Normalize agent content `type` (e.g. "text", "output_text") to UI payload type.
 */
export function normalizeResponseContentType(t?: string): ChatResponseContentType {
  const v = String(t || "").toLowerCase();
  if (v === "table" || v.includes("table")) return "table";
  if (v === "chart" || v.includes("chart")) return "chart";
  if (v === "json" || v.includes("json")) return "json";
  if (v === "html" || v.includes("html")) return "html";
  if (v === "error" || v.includes("error")) return "error";
  if (v === "widget-draft" || v.includes("widget")) return "widget-draft";
  if (v === "mutation-draft" || v.includes("mutation")) return "mutation-draft";
  if (v === "forecast-plan" || v === "forecast_plan") return "forecast-plan";
  if (v === "forecast-result" || v === "forecast_result" || v === "prediction-result") return "forecast-result";
  if (v.includes("forecast-plan") || v.includes("forecast_plan")) return "forecast-plan";
  if (v.includes("forecast-result") || v.includes("prediction")) return "forecast-result";
  if (v === "clarification" || v.includes("clarification")) return "clarification";
  return "text";
}

type ExtractedStructuredText = {
  type?: string;
  message?: string;
  result?: unknown;
  widgetDraft?: unknown;
  mutationDraft?: unknown;
};

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (value == null || typeof value !== "object" || Array.isArray(value)) return undefined;
  return value as Record<string, unknown>;
}

/**
 * Broker / orchestrator often sends JSON as a string (sometimes double-encoded). Unwrap recursively.
 */
export function parseFlexibleJsonDeep(value: unknown, maxDepth = 6): unknown {
  let current: unknown = value;
  let depth = 0;
  while (depth < maxDepth && typeof current === "string") {
    const s = current.trim();
    if (!s || (!(s.startsWith("{") || s.startsWith("[")))) {
      break;
    }
    try {
      current = JSON.parse(s) as unknown;
      depth++;
    } catch {
      break;
    }
  }
  return current;
}

/** True when a string looks like a stringified `{ type, message, result }` agent envelope. */
function isJsonEnvelopeString(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const s = value.trim();
  return s.startsWith("{") && /"type"\s*:/.test(s);
}

function unescapeJsonStringFragment(fragment: string): string {
  return fragment
    .replace(/\\"/g, '"')
    .replace(/\\n/g, "\n")
    .replace(/\\t/g, "\t")
    .replace(/\\\\/g, "\\");
}

function repairTruncatedTableHtml(html: string): string {
  let out = html;
  const openTr = (out.match(/<tr\b/gi) || []).length;
  const closeTr = (out.match(/<\/tr>/gi) || []).length;
  for (let i = closeTr; i < openTr; i++) out += "</tr>";
  if (out.includes("<tbody") && !out.includes("</tbody>")) out += "</tbody>";
  if (!out.includes("</table>")) out += "</table>";
  return out;
}

/**
 * Recover structured payloads when formatter double-wraps JSON as type=text or truncates mid-string.
 */
function extractLooseEmbeddedPayload(value: unknown): {
  type: ChatResponseContentType;
  message?: string;
  result?: unknown;
  widgetDraft?: unknown;
} | null {
  if (typeof value !== "string") return null;
  const s = value.trim();
  if (!isJsonEnvelopeString(s)) return null;

  const typeMatch = s.match(/"type"\s*:\s*"([^"\\]+)"/);
  if (!typeMatch) return null;
  const innerType = normalizeResponseContentType(typeMatch[1]);
  if (innerType === "text") return null;

  const msgMatch = s.match(/"message"\s*:\s*"((?:\\.|[^"\\])*)"/);
  let innerMessage: string | undefined;
  if (msgMatch) {
    try {
      innerMessage = JSON.parse(`"${msgMatch[1]}"`) as string;
    } catch {
      innerMessage = unescapeJsonStringFragment(msgMatch[1]);
    }
  }

  if (innerType === "table" || innerType === "html") {
    const tableIdx = s.indexOf("<table");
    if (tableIdx >= 0) {
      let html = unescapeJsonStringFragment(s.slice(tableIdx));
      const closeIdx = html.indexOf("</table>");
      html = closeIdx >= 0 ? html.slice(0, closeIdx + 8) : repairTruncatedTableHtml(html);
      return {
        type: innerType,
        message: innerMessage,
        result: { response: html },
      };
    }
  }

  return null;
}

function resolveHumanMessage(
  candidate: unknown,
  fallback: string | undefined
): string | undefined {
  if (typeof candidate === "string") {
    const trimmed = candidate.trim();
    if (trimmed && !isJsonEnvelopeString(trimmed)) return trimmed;
  }
  return fallback;
}

function statusMeansCompleted(status: unknown): boolean {
  if (status == null || status === "") return true;
  const s = String(status).toLowerCase();
  return s === "completed" || s === "complete" || s === "success" || s === "finished";
}

function isAssistantMessageEnvelope(m: Record<string, unknown>): boolean {
  if (m.role === "assistant") return true;
  if (m.type === "message") return true;
  if ((m.type == null || m.type === "") && (m.role == null || m.role === "") && Array.isArray(m.content)) {
    return true;
  }
  return false;
}

function coerceTextBlockInner(textUnknown: unknown): Record<string, unknown> | undefined {
  const unfolded = parseFlexibleJsonDeep(textUnknown);
  return asRecord(unfolded);
}

function consumeStructuredContentBlock(block: Record<string, unknown>): ExtractedStructuredText | undefined {
  const typeHint = typeof block.type === "string" ? block.type : "";
  const hasTextKey = "text" in block && block.text != null;
  const looksLikeOutputText =
    typeHint === "output_text" || (typeHint === "text" && hasTextKey) || (hasTextKey && typeHint === "");

  if (!looksLikeOutputText && !hasTextKey) return undefined;

  const t = coerceTextBlockInner(block.text);
  if (!t) return undefined;

  const innerType = typeof t.type === "string" ? t.type : "text";
  return {
    type: innerType,
    message: typeof t.message === "string" ? t.message : undefined,
    result: t.result,
    widgetDraft: t.widgetDraft,
  };
}

/**
 * Parses OpenAI-style / agent payloads: `{ output: [{ ..., content: [{ type: "output_text", text: { type, message, result } }] }] }`
 */
export function extractStructuredOutput(payload: Record<string, unknown>): ExtractedStructuredText | undefined {
  const output = payload.output;
  if (!Array.isArray(output) || output.length === 0) return undefined;

  for (let i = output.length - 1; i >= 0; i--) {
    const msg = output[i];
    if (!msg || typeof msg !== "object") continue;
    const m = msg as Record<string, unknown>;

    if (!isAssistantMessageEnvelope(m)) continue;
    if (!statusMeansCompleted(m.status)) continue;

    const content = m.content;
    if (!Array.isArray(content) || content.length === 0) continue;

    for (let j = content.length - 1; j >= 0; j--) {
      const block = content[j];
      if (!block || typeof block !== "object") continue;
      const b = block as Record<string, unknown>;
      const parsed = consumeStructuredContentBlock(b);
      if (parsed) return parsed;
    }
  }

  return undefined;
}

const ENVELOPE_KEYS = ["data", "payload", "body", "response", "result"] as const;

/**
 * WS body may duplicate the REST shape (`data.payload`) while history stores bare `output`. Try each envelope.
 */
export function extractStructuredOutputFromFlexibleBody(body: unknown): ExtractedStructuredText | undefined {
  const unfolded = parseFlexibleJsonDeep(body);
  const root = asRecord(unfolded);
  if (!root) return undefined;

  const candidates: Record<string, unknown>[] = [];
  const seen = new Set<unknown>();
  const add = (rec: Record<string, unknown> | undefined) => {
    if (!rec || seen.has(rec)) return;
    seen.add(rec);
    candidates.push(rec);
  };

  add(root);
  for (const key of ENVELOPE_KEYS) {
    const inner = asRecord(parseFlexibleJsonDeep(root[key]));
    add(inner);
    if (inner) {
      for (const key2 of ENVELOPE_KEYS) {
        add(asRecord(parseFlexibleJsonDeep(inner[key2])));
      }
    }
  }

  const preferWithOutputFirst = [...candidates].sort((a, b) =>
    Number(Array.isArray(b.output)) - Number(Array.isArray(a.output))
  );

  for (const rec of preferWithOutputFirst) {
    const extracted = extractStructuredOutput(rec);
    if (extracted) return extracted;
  }

  return undefined;
}

function readQuotaFromResult(result: unknown): {
  quotaExceeded?: boolean;
  period?: "daily" | "weekly" | "monthly" | "no_subscription";
  resetDate?: string;
} {
  if (!result || typeof result !== "object") return {};
  const o = result as Record<string, unknown>;
  const periodRaw = o.period;
  const period =
    periodRaw === "daily" ||
    periodRaw === "weekly" ||
    periodRaw === "monthly" ||
    periodRaw === "no_subscription"
      ? periodRaw
      : undefined;
  return {
    quotaExceeded: typeof o.quotaExceeded === "boolean" ? o.quotaExceeded : undefined,
    period,
    resetDate: typeof o.resetDate === "string" ? o.resetDate : undefined,
  };
}

/**
 * Formatter / broker may wrap agent JSON as type=text with stringified body in `result`.
 * Promote inner widget-draft / mutation-draft (and similar) to top-level fields the UI understands.
 */
export function promoteEmbeddedStructuredPayload(
  type: ChatResponseContentType,
  message: string | undefined,
  result: unknown,
  widgetDraft: unknown,
  mutationDraft?: unknown
): {
  type: ChatResponseContentType;
  message?: string;
  result?: unknown;
  widgetDraft?: unknown;
  mutationDraft?: unknown;
} {
  const tryPromote = (value: unknown) => {
    const parsed = parseFlexibleJsonDeep(value);
    const rec = asRecord(parsed);
    if (!rec) {
      return extractLooseEmbeddedPayload(value);
    }

    const innerTypeRaw = typeof rec.type === "string" ? rec.type : undefined;
    const innerType = innerTypeRaw ? normalizeResponseContentType(innerTypeRaw) : undefined;
    const innerDraft =
      rec.widgetDraft !== undefined
        ? parseFlexibleJsonDeep(rec.widgetDraft)
        : rec.dataSource && typeof rec.dataSource === "object"
          ? rec
          : undefined;
    const innerMutation =
      rec.mutationDraft !== undefined ? parseFlexibleJsonDeep(rec.mutationDraft) : undefined;

    if (innerType === "mutation-draft" || innerMutation) {
      return {
        type: "mutation-draft" as const,
        message: resolveHumanMessage(rec.message, message),
        result: rec,
        widgetDraft: undefined,
        mutationDraft: innerMutation,
      };
    }

    if (innerType === "widget-draft" || innerDraft) {
      return {
        type: "widget-draft" as const,
        message: resolveHumanMessage(rec.message, message),
        result: rec,
        widgetDraft: innerDraft,
        mutationDraft: undefined,
      };
    }

    if (innerType && innerType !== "text") {
      return {
        type: innerType,
        message: resolveHumanMessage(rec.message, message),
        result: rec.result !== undefined ? parseFlexibleJsonDeep(rec.result) : rec,
        widgetDraft: undefined,
        mutationDraft: undefined,
      };
    }

    if (innerType === "text" && rec.result !== undefined) {
      return {
        type: "text" as const,
        message: resolveHumanMessage(rec.message, message),
        result: parseFlexibleJsonDeep(rec.result),
        widgetDraft: undefined,
        mutationDraft: undefined,
      };
    }

    return extractLooseEmbeddedPayload(value);
  };

  if (type === "mutation-draft" || mutationDraft !== undefined) {
    return { type: "mutation-draft", message, result, mutationDraft };
  }

  if (type === "widget-draft" || widgetDraft !== undefined) {
    return { type: "widget-draft", message, result, widgetDraft };
  }

  for (const candidate of [result, message]) {
    const promoted = tryPromote(candidate);
    if (promoted) return promoted;
  }

  return { type, message, result, widgetDraft, mutationDraft };
}

function looksLikeAgentEnvelope(rec: Record<string, unknown>): boolean {
  return (
    typeof rec.type === "string" &&
    (rec.result !== undefined ||
      rec.widgetDraft !== undefined ||
      rec.mutationDraft !== undefined ||
      typeof rec.message === "string")
  );
}

/** Peel nested `{ type, message, result }` envelopes left by formatters or history storage. */
export function finalizeChatPayload(
  type: ChatResponseContentType,
  message: string | undefined,
  result: unknown,
  widgetDraft: unknown,
  mutationDraft?: unknown
): {
  type: ChatResponseContentType;
  message?: string;
  result?: unknown;
  widgetDraft?: unknown;
  mutationDraft?: unknown;
} {
  let currentType = type;
  let currentMessage = message;
  let currentResult = result;
  let currentDraft = widgetDraft;
  let currentMutation = mutationDraft;

  for (let depth = 0; depth < 8; depth++) {
    const promoted = promoteEmbeddedStructuredPayload(
      currentType,
      currentMessage,
      currentResult,
      currentDraft,
      currentMutation
    );
    const changed =
      promoted.type !== currentType ||
      promoted.message !== currentMessage ||
      promoted.result !== currentResult ||
      promoted.widgetDraft !== currentDraft ||
      promoted.mutationDraft !== currentMutation;
    if (changed) {
      currentType = promoted.type;
      currentMessage = promoted.message;
      currentResult = promoted.result;
      currentDraft = promoted.widgetDraft;
      currentMutation = promoted.mutationDraft;
      continue;
    }

    const rec = asRecord(parseFlexibleJsonDeep(currentResult));
    if (!rec || !looksLikeAgentEnvelope(rec)) {
      break;
    }

    const innerType = normalizeResponseContentType(String(rec.type));
    if (typeof rec.message === "string" && rec.message.trim() && !currentMessage) {
      currentMessage = rec.message;
    } else if (typeof rec.message === "string" && rec.message.trim() && currentMessage === rec.message) {
      // same message — still unwrap result
    }

    if (rec.result !== undefined) {
      currentResult = parseFlexibleJsonDeep(rec.result);
      if (innerType !== "text" && innerType !== currentType) {
        currentType = innerType;
      }
      continue;
    }

    break;
  }

  if (Array.isArray(currentResult) && currentResult.length > 0) {
    const parts = currentResult
      .map((item) => extractTextFromResult(item))
      .map((s) => s.trim())
      .filter(Boolean);
    const unique = [...new Set(parts)];
    if (unique.length === 1) {
      currentResult = { response: unique[0] };
    } else if (unique.length > 1) {
      currentResult = { response: unique.join("\n\n") };
    }
  }

  if (typeof currentMessage === "string" && isJsonEnvelopeString(currentMessage)) {
    const loose = extractLooseEmbeddedPayload(currentMessage);
    if (loose) {
      if (loose.type !== "text" && (currentType === "text" || loose.type !== currentType)) {
        currentType = loose.type;
      }
      currentMessage = loose.message ?? undefined;
      if (loose.result !== undefined) {
        currentResult = loose.result;
      }
    } else {
      currentMessage = undefined;
    }
  }

  if (
    currentType === "text" &&
    currentResult &&
    typeof currentResult === "object" &&
    typeof (currentResult as Record<string, unknown>).response === "string" &&
    String((currentResult as Record<string, unknown>).response)
      .trim()
      .startsWith("<table")
  ) {
    currentType = "table";
  }

  return {
    type: currentType,
    message: currentMessage,
    result: currentResult,
    widgetDraft: currentDraft,
    mutationDraft: currentMutation,
  };
}

/**
 * Extract human-readable text from chat `result` blobs (unwraps nested agent JSON envelopes).
 */
export function extractTextFromResult(result: unknown): string {
  if (result == null) return "";

  if (typeof result === "string") {
    const trimmed = result.trim();
    if (
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    ) {
      const parsed = parseFlexibleJsonDeep(result);
      if (parsed !== result) {
        const inner = extractTextFromResult(parsed);
        if (inner) return inner;
      }
    }
    return result;
  }

  if (Array.isArray(result)) {
    const parts = result.map((item) => extractTextFromResult(item)).filter(Boolean);
    const unique = [...new Set(parts)];
    return unique.join("\n\n");
  }

  const rec = asRecord(parseFlexibleJsonDeep(result));
  if (!rec) return String(result);

  if (typeof rec.response === "string" && rec.response.trim()) {
    return rec.response;
  }

  if (looksLikeAgentEnvelope(rec) && rec.result !== undefined) {
    const inner = extractTextFromResult(rec.result);
    if (inner) return inner;
  }

  if (typeof rec.text === "string" && rec.text.trim()) {
    return rec.text;
  }

  return "";
}

export interface ChatHistoryMessageLike {
  messageId?: string;
  orchestrationResponse?: unknown;
  responseType?: string;
  status?: string;
  errorMessage?: string;
  createdAt?: string;
}

export interface ChatHistoryContextLike {
  username?: string;
  correlationId?: string;
  createdDate?: string;
}

function looksLikeNestedStorageMap(rec: Record<string, unknown>): boolean {
  const inner = asRecord(parseFlexibleJsonDeep(rec.result));
  return (
    typeof rec.type === "string" &&
    rec.result !== undefined &&
    inner != null &&
    typeof inner.type === "string" &&
    inner.result !== undefined
  );
}

/**
 * Unwraps legacy history rows where {@code toStorageMap()} was persisted inside {@code result}.
 */
function unwrapHistoryStorageEnvelope(parsed: unknown): unknown {
  const rec = asRecord(parseFlexibleJsonDeep(parsed));
  if (!rec) return parsed;

  if (looksLikeNestedStorageMap(rec)) {
    const inner = asRecord(parseFlexibleJsonDeep(rec.result))!;
    const outerType =
      typeof rec.type === "string" ? normalizeResponseContentType(rec.type) : undefined;
    const innerType =
      typeof inner.type === "string" ? normalizeResponseContentType(inner.type) : undefined;
    const resolvedType =
      outerType === "text" && innerType && innerType !== "text"
        ? innerType
        : innerType ?? outerType;
    return {
      type: resolvedType,
      message: resolveHumanMessage(inner.message, resolveHumanMessage(rec.message, undefined)),
      result: inner.result,
      widgetDraft: rec.widgetDraft ?? inner.widgetDraft,
      correlationId: rec.correlationId ?? inner.correlationId,
      username: rec.username ?? inner.username,
    };
  }

  if (typeof rec.type === "string") {
    return rec;
  }

  return rec;
}

/**
 * Replays a persisted chat message through the same normalizer used for live WebSocket payloads.
 */
export function normalizeChatHistoryMessage(
  m: ChatHistoryMessageLike,
  context?: ChatHistoryContextLike
): ChatOrchestrationWebSocketPayload {
  const parsed = parseFlexibleJsonDeep(m.orchestrationResponse);
  const rec = asRecord(parsed);

  if (!rec) {
    return {
      username: context?.username,
      correlationId: context?.correlationId || "",
      type: m.responseType ? normalizeResponseContentType(m.responseType) : "text",
      message: undefined,
      result: parsed ?? m.orchestrationResponse,
      error: m.status === "ERROR" ? m.errorMessage : undefined,
      timestamp: m.createdAt || context?.createdDate || new Date().toISOString(),
    };
  }

  let envelope: unknown = unwrapHistoryStorageEnvelope(rec);
  const envRec = asRecord(envelope);
  if (envRec && typeof envRec.type !== "string" && m.responseType) {
    envelope = { type: m.responseType, result: envRec };
  } else if (envRec && typeof envRec.type !== "string" && envRec.result === undefined) {
    envelope = { type: m.responseType || "text", result: envRec };
  }

  const base = normalizeChatWebSocketBody(envelope);

  let type = base.type || "text";
  if (m.responseType) {
    const fromDb = normalizeResponseContentType(m.responseType);
    if (fromDb !== "text" && (type === "text" || fromDb !== type)) {
      type = fromDb;
    }
  }

  let result = base.result;
  let widgetDraft = base.widgetDraft;
  let mutationDraft = base.mutationDraft;
  let message = base.message;

  if (type === "text") {
    const promoted = promoteEmbeddedStructuredPayload("text", message, result, widgetDraft, mutationDraft);
    if (promoted.type !== "text" || promoted.message !== message || promoted.result !== result) {
      type = promoted.type;
      result = promoted.result;
      widgetDraft = promoted.widgetDraft;
      mutationDraft = promoted.mutationDraft;
      message = promoted.message ?? message;
    }
  }

  const finalized = finalizeChatPayload(
    type === "auto-selection" || type === "progress" || type === "warehouse-job" || type === "chat-run"
      ? "text"
      : type,
    message,
    result,
    widgetDraft,
    mutationDraft
  );
  type = finalized.type;
  message = finalized.message;
  result = finalized.result;
  widgetDraft = finalized.widgetDraft;
  mutationDraft = finalized.mutationDraft;

  const quotaMeta = result && typeof result === "object" ? (result as Record<string, unknown>) : undefined;

  return {
    ...base,
    username: context?.username || base.username,
    correlationId: context?.correlationId || base.correlationId || "",
    type,
    message,
    result,
    widgetDraft,
    mutationDraft,
    error:
      m.status === "ERROR"
        ? m.errorMessage || base.error
        : base.error,
    quotaExceeded: Boolean(base.quotaExceeded ?? quotaMeta?.quotaExceeded),
    period:
      quotaMeta?.period === "daily" ||
      quotaMeta?.period === "weekly" ||
      quotaMeta?.period === "monthly" ||
      quotaMeta?.period === "no_subscription"
        ? (quotaMeta.period as "daily" | "weekly" | "monthly" | "no_subscription")
        : base.period,
    resetDate: typeof quotaMeta?.resetDate === "string" ? quotaMeta.resetDate : base.resetDate,
    timestamp: m.createdAt || context?.createdDate || base.timestamp || new Date().toISOString(),
    messageId:
      typeof m.messageId === "string"
        ? m.messageId
        : base.messageId,
  };
}

/**
 * Unifies legacy flat payloads `{ type, message, result }` and nested `output` agent responses into `ChatOrchestrationWebSocketPayload`.
 */
export function normalizeChatWebSocketBody(body: unknown): ChatOrchestrationWebSocketPayload {
  const now = new Date().toISOString();
  const unfoldedBody = parseFlexibleJsonDeep(body);
  const raw = asRecord(unfoldedBody);
  if (!raw) {
    if (typeof body === "string" && body.trim() !== "") {
      return {
        correlationId: "",
        type: "text",
        message: undefined,
        result: { response: body },
        timestamp: now,
      };
    }
    return { correlationId: "", timestamp: now };
  }

  const extracted =
    extractStructuredOutputFromFlexibleBody(raw) ??
    extractStructuredOutput(raw);

  const type = normalizeResponseContentType(
    extracted?.type ?? (typeof raw.type === "string" ? raw.type : undefined)
  );
  const message =
    extracted?.message ??
    (typeof raw.message === "string" ? raw.message : undefined);
  let result =
    extracted?.result !== undefined
      ? extracted.result
      : raw.result !== undefined
        ? parseFlexibleJsonDeep(raw.result)
        : undefined;

  if (result === undefined) {
    const resp = raw.response;
    if (typeof resp === "string" || (resp != null && typeof resp === "object")) {
      result = parseFlexibleJsonDeep(resp);
    }
  }

  const fromRawQuota = readQuotaFromResult(raw);
  const fromResultQuota = readQuotaFromResult(result);

  const widgetDraft =
    extracted?.widgetDraft !== undefined
      ? parseFlexibleJsonDeep(extracted.widgetDraft)
      : raw.widgetDraft !== undefined
        ? parseFlexibleJsonDeep(raw.widgetDraft)
        : result &&
            typeof result === "object" &&
            (result as Record<string, unknown>).widgetDraft !== undefined
          ? parseFlexibleJsonDeep((result as Record<string, unknown>).widgetDraft)
          : undefined;

  const mutationDraft =
    extracted?.mutationDraft !== undefined
      ? parseFlexibleJsonDeep(extracted.mutationDraft)
      : raw.mutationDraft !== undefined
        ? parseFlexibleJsonDeep(raw.mutationDraft)
        : result &&
            typeof result === "object" &&
            (result as Record<string, unknown>).mutationDraft !== undefined
          ? parseFlexibleJsonDeep((result as Record<string, unknown>).mutationDraft)
          : undefined;

  if (widgetDraft !== undefined && result === undefined) {
    result = { widgetDraft };
  }
  if (mutationDraft !== undefined && result === undefined) {
    result = { mutationDraft };
  }

  const promoted = promoteEmbeddedStructuredPayload(type, message, result, widgetDraft, mutationDraft);
  const finalized = finalizeChatPayload(
    promoted.type,
    promoted.message,
    promoted.result,
    promoted.widgetDraft,
    promoted.mutationDraft
  );

  const rawType = typeof raw.type === "string" ? raw.type.trim().toLowerCase() : "";
  if (rawType === "progress" || rawType === "auto-selection" || rawType === "warehouse-job" || rawType === "chat-run") {
    return {
      username: typeof raw.username === "string" ? raw.username : undefined,
      correlationId: typeof raw.correlationId === "string" ? raw.correlationId : "",
      type: rawType,
      message: typeof raw.message === "string" ? raw.message : undefined,
      result: raw.result !== undefined ? parseFlexibleJsonDeep(raw.result) : undefined,
      autoSelection:
        (asRecord(parseFlexibleJsonDeep(raw.autoSelection)) as ChatAutoSelection | undefined) ??
        undefined,
      timestamp: typeof raw.timestamp === "string" ? raw.timestamp : now,
      messageId: typeof raw.messageId === "string" ? raw.messageId : undefined,
    };
  }

  const autoSelection = asRecord(parseFlexibleJsonDeep(raw.autoSelection)) as
    | ChatAutoSelection
    | undefined;
  const executedQueriesRaw = parseFlexibleJsonDeep(raw.executedQueries);
  const executedQueries: ChatExecutedQuery[] | undefined = Array.isArray(executedQueriesRaw)
    ? executedQueriesRaw
        .map((item) => asRecord(item))
        .filter((item): item is Record<string, unknown> => !!item)
        .map((item): ChatExecutedQuery | null => {
          const kind = String(item.kind || "opensearch").toLowerCase();
          if (kind !== "opensearch" && kind !== "sql" && kind !== "mongo") return null;
          const str = (v: unknown) => (typeof v === "string" && v.trim() ? v : undefined);
          return {
            kind,
            tool: str(item.tool),
            indexName: str(item.indexName) ?? str(item.index),
            searchType: str(item.searchType),
            adaptorId: str(item.adaptorId),
            adaptorName: str(item.adaptorName),
            adaptorType: str(item.adaptorType),
            query: item.query ?? item.dsl ?? item.dslTemplate,
          };
        })
        .filter((item): item is ChatExecutedQuery => {
          if (!item || item.query == null) return false;
          return item.kind === "opensearch" ? !!item.indexName : !!item.adaptorId;
        })
    : undefined;

  return {
    username: typeof raw.username === "string" ? raw.username : undefined,
    correlationId: typeof raw.correlationId === "string" ? raw.correlationId : "",
    type: finalized.type,
    message: finalized.message,
    result: finalized.result,
    widgetDraft: finalized.widgetDraft,
    mutationDraft: finalized.mutationDraft,
    autoSelection: autoSelection ?? undefined,
    executedQueries: executedQueries && executedQueries.length > 0 ? executedQueries : undefined,
    vpnOtpRequired: parseVpnOtpRequired(raw.vpnOtpRequired),
    error: typeof raw.error === "string" ? raw.error : undefined,
    quotaExceeded:
      typeof raw.quotaExceeded === "boolean"
        ? raw.quotaExceeded
        : Boolean(fromResultQuota.quotaExceeded ?? fromRawQuota.quotaExceeded),
    period: fromRawQuota.period ?? fromResultQuota.period,
    resetDate: fromRawQuota.resetDate ?? fromResultQuota.resetDate,
    timestamp: typeof raw.timestamp === "string" ? raw.timestamp : now,
    messageId: typeof raw.messageId === "string" ? raw.messageId : undefined,
  };
}

function parseVpnOtpRequired(value: unknown): ChatOrchestrationWebSocketPayload["vpnOtpRequired"] {
  const rec = asRecord(parseFlexibleJsonDeep(value));
  const id = rec && typeof rec.vpnProfileId === "string" ? rec.vpnProfileId.trim() : "";
  if (!id) return undefined;
  return {
    vpnProfileId: id,
    vpnProfileName: typeof rec?.vpnProfileName === "string" ? rec.vpnProfileName : undefined,
  };
}

export interface ChatSendResult {
  correlationId: string;
  messageId?: string;
}

/**
 * Send chat message via REST (POST /api/chat/send).
 * Token via getAccessToken() (same as axios). Auth: Authorization: Bearer <token>.
 */
export async function sendChatMessage(
  message: string,
  correlationId?: string,
  indices?: string[],
  outputPreference?: { outputType: string; widgetFormat?: string; chartType?: string },
  pageSummary?: PageSummary
): Promise<ChatSendResult> {
  const token = await getAccessToken();
  if (!token) {
    throw new Error("Not authenticated");
  }
  const key = embedRuntimeKey();
  const resolvedSummary =
    pageSummary ||
    (isEmbedPageContentEnabled() && key && correlationId
      ? embedPageSummaryForSend(key, correlationId)
      : undefined);
  const res = await fetch(`${API_BASE}/api/chat/send`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`, ...embedRequestHeaders(),
    },
    body: JSON.stringify({
      message,
      correlationId,
      ...(indices && indices.length > 0 ? { indices } : {}),
      ...(outputPreference ? { outputPreference } : {}),
      ...(embedHostContext() ? { hostContext: embedHostContext() } : {}),
      ...(resolvedSummary ? { pageSummary: resolvedSummary } : {}),
    }),
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`Send failed: ${res.status} ${errText || res.statusText}`);
  }
  const data = await res.json();
  if (!data?.correlationId) {
    throw new Error("No correlationId in response");
  }
  return {
    correlationId: data.correlationId as string,
    messageId: typeof data.messageId === "string" ? data.messageId : undefined,
  };
}

export type ChatSuggestRecentMessage = {
  role: "user" | "assistant";
  content: string;
};

export type ChatSuggestPromptsParams = {
  message?: string;
  correlationId?: string;
  indices?: string[];
  locale?: string;
  recentMessages?: ChatSuggestRecentMessage[];
  openApiCatalogId?: string;
  openApiTag?: string;
};

/**
 * Sync lexical FINAL output-type prediction (POST /api/chat/predict-intent).
 */
export async function predictChatOutputIntent(
  message: string
): Promise<ChatOutputIntentPrediction> {
  const token = await getAccessToken();
  if (!token) {
    throw new Error("Not authenticated");
  }
  const res = await fetch(`${API_BASE}/api/chat/predict-intent`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`, ...embedRequestHeaders(),
    },
    body: JSON.stringify({ message: message ?? "" }),
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`Predict intent failed: ${res.status} ${errText || res.statusText}`);
  }
  const data = (await res.json()) as ChatOutputIntentPrediction;
  return {
    outputType: typeof data?.outputType === "string" ? data.outputType : undefined,
    chartType: typeof data?.chartType === "string" ? data.chartType : undefined,
    confidence: typeof data?.confidence === "number" ? data.confidence : undefined,
    scores: data?.scores && typeof data.scores === "object" ? data.scores : undefined,
    source: "predicted",
  };
}

/**
 * Sync LLM prompt suggestions for the composer (POST /api/chat/suggest-prompts).
 */
export async function fetchSuggestedPrompts(
  params: ChatSuggestPromptsParams = {}
): Promise<string[]> {
  const token = await getAccessToken();
  if (!token) {
    throw new Error("Not authenticated");
  }
  const res = await fetch(`${API_BASE}/api/chat/suggest-prompts`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`, ...embedRequestHeaders(),
    },
    body: JSON.stringify({
      ...(params.message != null && params.message.trim()
        ? { message: params.message.trim() }
        : {}),
      ...(params.correlationId ? { correlationId: params.correlationId } : {}),
      ...(params.indices && params.indices.length > 0 ? { indices: params.indices } : {}),
      ...(params.locale ? { locale: params.locale } : {}),
      ...(params.recentMessages && params.recentMessages.length > 0
        ? { recentMessages: params.recentMessages }
        : {}),
      ...(params.openApiCatalogId ? { openApiCatalogId: params.openApiCatalogId } : {}),
      ...(params.openApiTag ? { openApiTag: params.openApiTag } : {}),
    }),
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`Suggest failed: ${res.status} ${errText || res.statusText}`);
  }
  const data = await res.json();
  const list = data?.suggestions;
  if (!Array.isArray(list)) {
    return [];
  }
  return list
    .filter((x): x is string => typeof x === "string" && x.trim().length > 0)
    .map((x) => x.trim())
    .slice(0, 5);
}

export type ChatMailDraftParams = {
  to: string;
  subject: string;
  body: string;
  html?: boolean;
  summary?: string;
  correlationId?: string;
};

/**
 * Creates a pending mail-send mutation draft (POST /api/chat/mail-draft).
 * Does not send until the user Approves in the chat UI.
 */
export async function createChatMailDraft(
  params: ChatMailDraftParams
): Promise<ChatOrchestrationWebSocketPayload> {
  const token = await getAccessToken();
  if (!token) {
    throw new Error("Not authenticated");
  }
  const res = await fetch(`${API_BASE}/api/chat/mail-draft`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`, ...embedRequestHeaders(),
    },
    body: JSON.stringify({
      to: params.to,
      subject: params.subject,
      body: params.body,
      ...(params.html != null ? { html: params.html } : {}),
      ...(params.summary ? { summary: params.summary } : {}),
      ...(params.correlationId ? { correlationId: params.correlationId } : {}),
    }),
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`Mail draft failed: ${res.status} ${errText || res.statusText}`);
  }
  const data = (await res.json()) as Record<string, unknown>;
  return {
    correlationId: String(data.correlationId || params.correlationId || ""),
    type: "mutation-draft",
    message: typeof data.message === "string" ? data.message : undefined,
    mutationDraft: data.mutationDraft,
    result: data,
    timestamp: new Date().toISOString(),
  };
}

export async function searchChatIndices(query: string): Promise<string[]> {
  const q = query.trim();
  if (q.length < 2) {
    return [];
  }
  const token = await getAccessToken();
  if (!token) {
    throw new Error("Not authenticated");
  }
  const params = new URLSearchParams({ q });
  const res = await fetch(`${API_BASE}/api/chat/indices?${params}`, {
    headers: { Authorization: `Bearer ${token}`, ...embedRequestHeaders() },
  });
  if (!res.ok) {
    throw new Error(`Failed to search indices: ${res.status}`);
  }
  const data = await res.json();
  if (!Array.isArray(data)) {
    return [];
  }
  return data
    .filter((x): x is string => typeof x === "string" && x.trim().length > 0)
    .map((x) => x.trim());
}

/**
 * Persists user-edited widget draft into chat history for a message exchange.
 */
export async function persistChatWidgetDraft(params: {
  messageId: string;
  correlationId: string;
  widgetDraft: unknown;
  message?: string;
  savedWidgetId?: string;
}): Promise<void> {
  const token = await getAccessToken();
  if (!token) {
    throw new Error("Not authenticated");
  }
  const res = await fetch(
    `${API_BASE}/chat-history/messages/${encodeURIComponent(params.messageId)}/widget-draft`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`, ...embedRequestHeaders(),
      },
      body: JSON.stringify({
        correlationId: params.correlationId,
        widgetDraft: params.widgetDraft,
        message: params.message,
        savedWidgetId: params.savedWidgetId,
      }),
    }
  );
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`Chat history update failed: ${res.status} ${errText || res.statusText}`);
  }
}

/** Max time to wait for an orchestration reply on /topic/orchestration-response/{correlationId}. */
export const CHAT_WS_RESPONSE_TIMEOUT_MS = 15 * 60 * 1000;

/** Passed to onError when the wait exceeds {@link CHAT_WS_RESPONSE_TIMEOUT_MS}. */
export const CHAT_RESPONSE_TIMEOUT_ERROR = "CHAT_RESPONSE_TIMEOUT";

/**
 * Subscribe to /topic/orchestration-response/{correlationId} over SockJS (ws-sockjs).
 * Token via getAccessToken(); auth only via query ?access_token=<token> (guide).
 * Deactivates client only on success (!body.error). Returns unsubscribe function.
 */
export async function subscribeToChatResponse(
  correlationId: string,
  onMessage: (payload: ChatOrchestrationWebSocketPayload) => void,
  onError?: (err: string) => void
): Promise<() => void> {
  const token = await getAccessToken();
  if (!token) {
    onError?.("Not authenticated");
    return () => {};
  }

  const topic = `/topic/orchestration-response/${correlationId}`;
  const wsUrlWithAuth = `${WS_SOCKJS}?access_token=${encodeURIComponent(token)}`;

  let settled = false;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const clearResponseTimeout = () => {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
      timeoutId = undefined;
    }
  };

  const settle = () => {
    if (settled) return;
    settled = true;
    clearResponseTimeout();
  };

  const startResponseTimeout = () => {
    clearResponseTimeout();
    timeoutId = setTimeout(() => {
      if (settled) return;
      settle();
      client.deactivate();
      onError?.(CHAT_RESPONSE_TIMEOUT_ERROR);
    }, CHAT_WS_RESPONSE_TIMEOUT_MS);
  };

  const client = new Client({
    webSocketFactory: () => new SockJS(wsUrlWithAuth) as any,
    onConnect: () => {
      client.subscribe(topic, (message) => {
        let payload: ChatOrchestrationWebSocketPayload;
        try {
          if (!message?.body) {
            return;
          }
          const parsed = JSON.parse(message.body) as unknown;
          payload = normalizeChatWebSocketBody(parsed);
        } catch {
          // Ignore malformed frames if we already delivered a final answer.
          if (!settled) {
            settle();
            onError?.("Invalid message body");
          }
          return;
        }
        try {
          const interimType = String(payload.type || "").toLowerCase();
          const isInterimFrame =
            interimType === "auto-selection" ||
            interimType === "progress" ||
            interimType === "warehouse-job" ||
            interimType === "chat-run";
          if (isInterimFrame) {
            onMessage(payload);
            return;
          }
          settle();
          onMessage(payload);
          if (!payload.error || payload.error === "QUOTA_EXCEEDED") {
            client.deactivate();
          }
        } catch (handlerErr) {
          console.warn("Chat WS onMessage handler failed", handlerErr);
          if (!settled) {
            settle();
            onError?.(
              handlerErr instanceof Error ? handlerErr.message : "Invalid message body"
            );
          }
        }
      });
    },
    onStompError: (frame) => {
      settle();
      onError?.(frame.headers?.message || "STOMP error");
    },
    onWebSocketError: () => {
      settle();
      onError?.("WebSocket connection error");
    },
  });

  client.activate();
  startResponseTimeout();

  return () => {
    settle();
    client.deactivate();
  };
}
